import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts/notification-context"
import { ApiQueryName } from "@/models/api"
import type { Meter } from "@/models/meter"
import { Site } from "@/models/site"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { useInvalidateQueries } from "@/services/utils/useInvalidateQueries"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

export interface MeterUpdate {
  meterCode: Meter["meterCode"]
  meterProvider: Meter["meterProvider"]
  siteId: string
  accountNumber?: Meter["accountNumber"]
}

export interface UpdateMeterInput {
  meter: Meter
  updates: MeterUpdate
}

const applyUpdates = (meter: Meter, updates: MeterUpdate): string[] => {
  const withRelations: string[] = []

  meter.meterCode = updates.meterCode
  meter.meterProvider = updates.meterProvider
  meter.accountNumber = updates.accountNumber ?? ""

  const currentSiteId = meter.site?.id ?? ""
  if (currentSiteId !== updates.siteId) {
    const newSite = new Site({ id: updates.siteId })
    newSite.isPersisted = true
    meter.site = newSite
    withRelations.push("site.id")
  }

  return withRelations
}

const persistMeterUpdates = async (
  meter: Meter,
  updates: MeterUpdate
): Promise<boolean> => {
  // Mutate a clone so the React Query–cached Spraypaint model stays intact.
  const draft = meter.dup()
  const withRelations = applyUpdates(draft, updates)
  return await draft.save({ with: withRelations })
}

export const useUpdateMeter = (
  options?: UseMutationOptions<boolean, Error, UpdateMeterInput>
) => {
  const { invalidateQueries } = useInvalidateQueries()
  const { setNotification } = useNotification()
  const { t } = useTranslation("common")

  const mutation = useMutation({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn: async ({ meter, updates }: UpdateMeterInput) => {
      return await persistMeterUpdates(meter, updates)
    },
    onError: (error, ...rest) => {
      setNotification({
        message: getErrorNotificationMessage(
          error,
          t("editMeter.updateMeterError")
        ),
        type: "error",
      })

      options?.onError?.(error, ...rest)
    },
    onSuccess: (data, ...rest) => {
      // Necessary because Spraypaint returns false on 422 errors
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
      if (data === false) {
        setNotification({
          message: t("editMeter.updateMeterError"),
          type: "error",
        })
        return
      }

      invalidateQueries([
        createQueryKey(ApiQueryName.Meter, "getOne"),
        createQueryKey(ApiQueryName.Meters, "getMany"),
      ])

      options?.onSuccess?.(data, ...rest)
    },
  })

  return useRenameQueryResult(mutation, "updateMeter")
}
